/**
 * ADR-074 §3.5 / §11 — the Email Validation surface. This is the parent that
 * owns the in-tab **Outbound | Inbound sub-strip** (the §11 locked mock's
 * `.subtabs` segmented control — visually distinct from the page-level
 * Flow Validator|Email Validation strip, which is the underline `InPageTabs`).
 *
 * Provenance (restoration, 25 June 2026): the §11 visual spec
 * (`Docs/Build Docs/MVP Build/Phase 5 Design Specs/email-validation-diagnostics-tab.html`)
 * locks an Outbound|Inbound sub-strip inside the Email Validation tab, with the
 * Inbound sub-tab present-but-deferred. The ADR-074 email-observability build
 * series shipped the Outbound list mounted directly under the page tab and
 * dropped this sub-strip chrome (the equivalence proofs covered email templates,
 * not this surface). This component restores the signed-off sub-strip.
 *
 *   • Outbound — the live, built surface (EmailValidationOutbound, unchanged).
 *   • Inbound  — present but DEFERRED. Inbound content (replies, read receipts,
 *     routed messages, the Comms Agent capsules in the mock) is Phase 2 / the
 *     Comms Agent (ADR-074 §2). Only the sub-tab shell + a clear deferred empty
 *     state ship here; NO inbound content is built (see guardrails in ADR-074).
 */
import { useState } from 'react';
import { EmailValidationOutbound } from './EmailValidationOutbound';
import type { OutboundEmailRow } from '../../lib/email-validation-api';

type Sub = 'outbound' | 'inbound';

interface Props {
  /** Whether the operator may read (mutating tier). Mirrors the server gate. */
  canRead: boolean;
  /** The page owns the PDF-export control; passed down to the Outbound surface. */
  renderExport?: (rows: OutboundEmailRow[]) => React.ReactNode;
}

/** Inbound is Phase 2 (ADR-074 §2). The sub-tab is present so the signed-off
 *  navigation chrome is honoured, but it renders only a deferred empty state —
 *  no inbound content is built here. */
function InboundDeferred() {
  return (
    <div className="ev-inbound-deferred" role="note">
      <div className="ev-inbound-deferred-icon" aria-hidden="true">✉</div>
      <h2 className="ev-inbound-deferred-title">Inbound — coming with the Comms Agent</h2>
      <p className="ev-inbound-deferred-body">
        Inbound email is a Phase&nbsp;2 surface. Replies, read receipts and routed
        messages are multi-message, interpreted flows owned by the Comms Agent, so
        they land here once that agentic stream ships — not as extra stages on a
        closed outbound flow.
      </p>
      <p className="ev-inbound-deferred-meta">Deferred to Phase 2 · ADR-074 §2</p>
    </div>
  );
}

export function EmailValidation({ canRead, renderExport }: Props) {
  const [sub, setSub] = useState<Sub>('outbound');

  return (
    <div className="ev-surface">
      {/* §11 sub-strip — Outbound | Inbound. Segmented-pill control (locked mock
          `.subtabs`), deliberately distinct from the page-level underline tabs. */}
      <div className="ev-subtabs" role="tablist" aria-label="Email Validation direction">
        <button
          type="button"
          role="tab"
          aria-selected={sub === 'outbound'}
          className={`ev-subtab${sub === 'outbound' ? ' ev-subtab-active' : ''}`}
          onClick={() => setSub('outbound')}
        >
          Outbound
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={sub === 'inbound'}
          className={`ev-subtab${sub === 'inbound' ? ' ev-subtab-active' : ''}`}
          onClick={() => setSub('inbound')}
        >
          Inbound
          <span className="ev-subtab-badge ev-subtab-badge-soon">Phase 2</span>
        </button>
      </div>

      {sub === 'outbound' && (
        <EmailValidationOutbound canRead={canRead} renderExport={renderExport} />
      )}
      {sub === 'inbound' && <InboundDeferred />}
    </div>
  );
}
